/**
 * 消息气泡：按 contentType 渲染文本 / 图片 / 视频 / 文件。
 * 101 文本；102 图片；104 视频；105 文件（其它仍显示 content 占位）。
 */
import { Flex, Image, theme, Typography } from "antd";
import type { ImMessage } from "../types";

type Props = {
  selfId: string;
  messages: ImMessage[];
};

function formatSize(n?: number): string {
  if (!n || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function ChatPanel({ selfId, messages }: Props) {
  const { token } = theme.useToken();

  if (messages.length === 0) {
    return (
      <Flex flex={1} align="center" justify="center">
        <Typography.Text type="secondary">选一个会话开始聊天</Typography.Text>
      </Flex>
    );
  }

  return (
    <Flex
      vertical
      flex={1}
      style={{ minHeight: 0, overflow: "auto", padding: token.padding }}
    >
      {messages.map((m) => {
        const mine = m.sendID === selfId;
        const bubbleBg = mine ? token.colorPrimary : token.colorBgContainer;
        const bubbleColor = mine ? token.colorTextLightSolid : token.colorText;
        const isImage = m.contentType === 102 && Boolean(m.mediaUrl);
        const isVideo = m.contentType === 104 && Boolean(m.mediaUrl);
        const isFile = m.contentType === 105;
        // 文本（含 emoji）或媒体缺 URL 时的兜底
        const showText =
          m.contentType === 101 ||
          m.contentType === 106 ||
          (!isImage && !isVideo && !isFile);

        return (
          <Flex
            key={m.clientMsgID}
            justify={mine ? "flex-end" : "flex-start"}
            style={{ marginBottom: token.marginXS }}
          >
            <Flex
              vertical
              style={{
                maxWidth: "68%",
                padding: "10px 14px",
                color: bubbleColor,
                background: bubbleBg,
                borderRadius: mine
                  ? "18px 18px 6px 18px"
                  : "18px 18px 18px 6px",
              }}
            >
              {!mine && m.senderNickname ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {m.senderNickname}
                </Typography.Text>
              ) : null}

              {isImage ? (
                <Image
                  src={m.mediaUrl}
                  alt="图片"
                  style={{ maxWidth: 220, borderRadius: 8 }}
                />
              ) : null}

              {isVideo ? (
                <video
                  src={m.mediaUrl}
                  poster={m.snapshotUrl || undefined}
                  controls
                  style={{
                    maxWidth: 260,
                    borderRadius: 8,
                    background: "#000",
                  }}
                />
              ) : null}

              {isFile ? (
                <a
                  href={m.mediaUrl || "#"}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "inherit", textDecoration: "underline" }}
                >
                  📎 {m.fileName || "附件"}
                  {m.fileSize ? ` (${formatSize(m.fileSize)})` : ""}
                </a>
              ) : null}

              {showText ? (
                <Typography.Text
                  style={{
                    color: "inherit",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {m.content}
                </Typography.Text>
              ) : null}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
}
